import { useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { can } from '../permissions';
import Icon from './Icon';
import InfoTip from './InfoTip';
import { LessonCard, LessonForm } from './LessonsView';
import {
  LESSON_CATEGORIES,
  LESSON_CATEGORY_LABELS,
  LESSON_KINDS,
  LESSON_KIND_LABELS,
  filterLessons,
  isWorkspaceLesson,
  lessonCountsByCategory,
  libraryLessons,
  type Lesson,
  type LessonCategory,
  type LessonKind,
} from '../lessons';

/**
 * THE WORKSPACE LESSONS LIBRARY — 3.19 Learning & Governance spine, L1 (the view).
 *
 * Every lesson across the workspace in one place, so a new project can find what
 * applies before it starts. Built to the approved mock (dev-preview/lessons,
 * screen 7): a 230px facet rail filtering by kind and category with live counts,
 * each row carrying its origin — a workspace-wide pin or the board it was raised
 * on — and the quiet "Starting a project?" reuse hook the later increment fills in.
 *
 * Cross-board and not project-gated (it sits in the Team group): the tenant-wide
 * `lessons` read already scopes it to what the person may see.
 */

export default function LessonsLibrary() {
  const lessons = useApp((s) => s.lessons);
  const profiles = useApp((s) => s.profiles);
  const projects = useApp((s) => s.projects);
  const role = useApp((s) => s.role);
  const user = useApp((s) => s.user);
  const deleteLesson = useApp((s) => s.deleteLesson);

  const [kind, setKind] = useState<LessonKind | undefined>(undefined);
  const [category, setCategory] = useState<LessonCategory | undefined>(undefined);
  const [form, setForm] = useState<null | { mode: 'add' } | { mode: 'edit'; lesson: Lesson }>(null);

  const all = libraryLessons(lessons);
  const filtered = filterLessons(all, { category, kind });
  const catCounts = lessonCountsByCategory(all);
  const kindCount = (k: LessonKind) => all.filter((l) => l.kind === k).length;

  const boardName = (boardId?: string) =>
    (boardId && projects.find((p) => p.id === boardId)?.name) || 'A project';

  // Any member may ADD a workspace lesson (the lessons RLS allows it), but only its
  // author or a workspace admin may change or remove one — so the library offers
  // edit/delete exactly where they would be allowed, and a board lesson's writers
  // use its own board tab. (A member who is neither still sees every lesson.)
  const me = user?.email?.toLowerCase() ?? '';
  const isAdmin = can(role, 'editSettings');
  const mayManage = (l: Lesson) => l.madeBy === me || isAdmin;
  const confirmDelete = async (id: string) => {
    const ok = await confirmDialog({
      title: 'Delete this lesson?',
      body: 'This can’t be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) void deleteLesson(id);
  };

  if (form) {
    return (
      <LessonForm
        key={form.mode === 'add' ? 'add' : form.lesson.id}
        mode={form.mode}
        lesson={form.mode === 'edit' ? form.lesson : undefined}
        contextName="Workspace"
        boardId={form.mode === 'edit' ? form.lesson.boardId : undefined}
        onClose={() => setForm(null)}
      />
    );
  }

  const bandLabel = `${category ? LESSON_CATEGORY_LABELS[category] : 'All categories'} · ${
    kind ? LESSON_KIND_LABELS[kind].toLowerCase() : 'all kinds'
  }`;

  return (
    <div className="gov">
      <div className="gov-head">
        <div className="gov-head-l">
          <div className="gov-title-row">
            <h1 className="fd-title">Lessons Library</h1>
            <span className="ihint">
              <InfoTip label="Lessons Library">
                Every lesson captured on any project in the workspace, plus the workspace-wide ones.
                Filter by kind and category to find what applies to the project you&rsquo;re about to
                start, so you carry the learning in rather than relearning it.
              </InfoTip>
            </span>
          </div>
          <p className="gov-sub">
            Every lesson across the workspace · filter to find what applies before you start
          </p>
        </div>
        <div className="gov-tools">
          <button className="btn btn-primary" onClick={() => setForm({ mode: 'add' })}>
            <Icon name="plus" size={15} />
            Add a workspace lesson
          </button>
        </div>
      </div>
      <div className="gov-rule" />

      <div className="lib">
        <aside className="facets">
          <div className="facet-h">Kind</div>
          <button className={`frow ${!kind ? 'on' : ''}`} onClick={() => setKind(undefined)}>
            <span>All kinds</span>
            <span className="fc">{all.length}</span>
          </button>
          {LESSON_KINDS.map((k) => (
            <button
              key={k}
              className={`frow ${kind === k ? 'on' : ''}`}
              onClick={() => setKind(kind === k ? undefined : k)}
            >
              <span>{LESSON_KIND_LABELS[k]}</span>
              <span className="fc">{kindCount(k)}</span>
            </button>
          ))}

          <div className="facet-h">Category</div>
          <button
            className={`frow ${!category ? 'on' : ''}`}
            onClick={() => setCategory(undefined)}
          >
            <span>All categories</span>
            <span className="fc">{all.length}</span>
          </button>
          {LESSON_CATEGORIES.map((c) => (
            <button
              key={c}
              className={`frow ${category === c ? 'on' : ''}`}
              onClick={() => setCategory(category === c ? undefined : c)}
            >
              <span>{LESSON_CATEGORY_LABELS[c]}</span>
              <span className="fc">{catCounts[c]}</span>
            </button>
          ))}
        </aside>

        <div>
          <div className="band">
            <span className="band-h">{bandLabel}</span>
            <span className="band-c">{filtered.length}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="gov-empty gov-empty--anchored" style={{ marginTop: 20 }}>
              <h2>Nothing matches this filter</h2>
              <p>
                No lessons in the workspace match the kind and category you&rsquo;ve chosen. Clear a
                filter to widen the search, or add the first one.
              </p>
            </div>
          ) : (
            filtered.map((l) => (
              <LessonCard
                key={l.id}
                lesson={l}
                profiles={profiles}
                onEdit={mayManage(l) ? () => setForm({ mode: 'edit', lesson: l }) : undefined}
                onDelete={mayManage(l) ? () => confirmDelete(l.id) : undefined}
                topExtra={
                  isWorkspaceLesson(l) ? (
                    <span className="lorigin ws">
                      <span className="pin" />
                      Workspace-wide
                    </span>
                  ) : (
                    <span className="lorigin">
                      <span className="pin" />
                      {boardName(l.boardId)}
                    </span>
                  )
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
